import React from 'react';
import { DeviceCreate } from '../../api/models';
import { TranslationKey, useI18n } from '../../contexts/I18nContext';
import { ActivityIcon, AuditIcon, BellIcon, CodeIcon, IconProps, InfoIcon, SettingsIcon, TemplatesIcon, TerminalIcon } from '../navigation/icons';
import { useDeviceDetail, DeviceDetailTab } from './useDeviceDetail';
import { DevicesDetailsBasic } from './devicesDetails/DevicesDetailsBasic';
import { DevicesDetailsSettings } from './devicesDetails/DevicesDetailsSettings';
import { DevicesDetailsRaw } from './devicesDetails/DevicesDetailsRaw';
import { DevicesDetailsConf } from './devicesDetails/DevicesDetailsConf';
import { DevicesDetailsHistory } from './devicesDetails/DevicesDetailsHistory';
import { DevicesDetailsMonitoring } from './devicesDetails/DevicesDetailsMonitoring';
import { DevicesDetailsEvents } from './devicesDetails/DevicesDetailsEvents';
import { DevicesDetailsProgramming } from './devicesDetails/DevicesDetailsProgramming';
import './DeviceDetailModal.css';

interface DeviceDetailModalProps {
  device: DeviceCreate | null;
  isOpen: boolean;
  onClose: () => void;
  onDeviceUpdated?: (device: DeviceCreate) => void;
}

interface NavItem {
  key: DeviceDetailTab;
  labelKey: TranslationKey;
  icon: React.FC<IconProps>;
  testId: string;
  visible: boolean;
}

/**
 * The device dialog: a header, a side navigation, one panel per tab.
 *
 * Every panel owns its state through its own hook and receives only the
 * shared core (device, detail, error). The panels stay mounted while the
 * dialog is open — a half-typed Settings form or a loaded V2/Conf payload
 * survives a tab switch — and are simply hidden when not active.
 *
 * Which tabs exist depends on the role (useClientAccess): Raw and V2/Conf
 * for the ANEP side, Programming for SuperAdmin and Admin; History,
 * Monitoring and Events for everyone who may open the gateway.
 */
export const DeviceDetailModal: React.FC<DeviceDetailModalProps> = ({ device, isOpen, onClose, onDeviceUpdated }) => {
  const { t } = useI18n();
  const core = useDeviceDetail(device, isOpen, onDeviceUpdated);
  const { activeTab, setActiveTab, rawTabVisible, canViewDeviceInternals, canProgramDevices, error, setError } = core;

  if (!isOpen || !device) return null;

  const navItems: NavItem[] = [
    { key: 'basic', labelKey: 'tabs.basic', icon: InfoIcon, testId: 'tab-device-basic', visible: true },
    { key: 'settings', labelKey: 'tabs.settings', icon: SettingsIcon, testId: 'tab-device-settings', visible: true },
    { key: 'raw', labelKey: 'tabs.raw', icon: CodeIcon, testId: 'tab-device-raw', visible: rawTabVisible !== false },
    { key: 'v2conf', labelKey: 'tabs.v2conf', icon: TemplatesIcon, testId: 'tab-device-v2conf', visible: canViewDeviceInternals },
    { key: 'history', labelKey: 'tabs.history', icon: AuditIcon, testId: 'device-history-tab-button', visible: true },
    { key: 'monitoring', labelKey: 'tabs.monitoring', icon: ActivityIcon, testId: 'tab-device-monitoring', visible: true },
    { key: 'events', labelKey: 'tabs.events', icon: BellIcon, testId: 'tab-device-events', visible: true },
    { key: 'programming', labelKey: 'tabs.programming', icon: TerminalIcon, testId: 'tab-device-programming', visible: canProgramDevices },
  ];

  const panel = (key: DeviceDetailTab, node: React.ReactNode) => (
    <div key={key} className="modal-panel" hidden={activeTab !== key} data-testid={`panel-device-${key}`}>
      {node}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="device-detail-modal device-detail-modal-v2" data-testid="modal-device-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-title-section">
            <h2>IMEI: {device.idDevice}</h2>
           
            
            <span className={`status-badge ${device.needsRefresh ? 'needs-refresh' : 'up-to-date'}`}>
              {device.needsRefresh ? t('devices.needsRefresh') : t('common.active')}
            </span>
          </div>
          <div className="header-actions">
            <button className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>
              ×
            </button>
          </div>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="modal-body">
          <nav className="modal-side-nav" aria-label={t('devices.deviceIdentity')}>
            {navItems.filter((item) => item.visible).map((item) => {
              const Icon = item.icon;
              const active = activeTab === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`side-nav-button${active ? ' active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  data-testid={item.testId}
                  onClick={() => setActiveTab(item.key)}
                >
                  <Icon />
                  <span>{t(item.labelKey)}</span>
                </button>
              );
            })}
          </nav>

          <div className="modal-main">
            {panel('basic', <DevicesDetailsBasic core={core} />)}
            {panel('settings', <DevicesDetailsSettings core={core} />)}
            {rawTabVisible !== false && panel('raw', <DevicesDetailsRaw core={core} />)}
            {canViewDeviceInternals && panel('v2conf', <DevicesDetailsConf core={core} active={activeTab === 'v2conf'} />)}
            {panel('history', <DevicesDetailsHistory deviceId={device.idDevice} />)}
            {panel('monitoring', <DevicesDetailsMonitoring core={core} active={activeTab === 'monitoring'} />)}
            {panel('events', <DevicesDetailsEvents />)}
            {canProgramDevices && panel('programming', <DevicesDetailsProgramming core={core} active={activeTab === 'programming'} />)}
          </div>
        </div>

        <div className="modal-actions">
          <div className="action-group-right">
            <button onClick={onClose} data-testid="btn-device-close" className="btn btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
